import { ReactNode, useMemo, useState } from 'react';

import { convertTearOutsToProductDetails } from '~/UI/components/organisms/SidePanelTearoutsTab/utils';
import { useCornerRadii } from '~/api/easy-estimator-api/corner-radii-api/hooks/useCornerRadii';
import { useCutoutLaborProducts } from '~/api/easy-estimator-api/cutout-labor-api/hooks/useCutoutLabor';
import { useDiagonalCorners } from '~/api/easy-estimator-api/diagonal-corners-api/useDiagonalCorners';
import { useEdges } from '~/api/easy-estimator-api/edges-api/useEdges';
import { useOptionalItems } from '~/api/easy-estimator-api/optional-items-api/useOptionalItems';
import { useWaterfallLabor } from '~/api/easy-estimator-api/waterfall-labor-api/useWaterfallLabor';
import { ProductType } from '~/core/enums';
import { useSink } from '~/core/hooks/useSink';
import { ProductCatalogMap, ProductDetails } from '~/core/models';
import {
  convertProductsToProductDetailsList,
  createTypeSafeContext,
} from '~/core/utils';
import { useAppSelector } from '~/redux/hooks';
import { selectUsableColors } from '~/redux/slices/color';
import { convertColorsToProductDetails } from '~/redux/slices/color/color.utils';
import {
  useCookingAppliancesContext,
  useDishwasherContext,
  useFaucetContext,
  useOptionalProductContext,
  useRefrigeratorContext,
} from '~contexts';
import { convertCutoutsToProductDetails } from '~organisms/SidePanelCutoutsTab/SidePanelCutoutsTab.utils';

interface ProductDetailsContextValue {
  productCatalog: ProductCatalogMap;
  restorableCatalog: ProductCatalogMap;
  setRestorableCatalog: (value: ProductCatalogMap) => void;
}

export const [useProductDetailsContext, ProductDetailsContext] =
  createTypeSafeContext<ProductDetailsContextValue>();

const emptyArray = [] satisfies ProductDetails[];

export const ProductDetailsContextProvider = ({
  children,
}: {
  children?: ReactNode;
}): JSX.Element => {
  const [restorableCatalog, setRestorableCatalog] = useState<
    Record<number, ProductDetails>
  >({});

  const colors = useAppSelector(selectUsableColors);
  const { sortedEdges: edges } = useEdges();
  const { diagonalCorners } = useDiagonalCorners();
  const { cornerRadii } = useCornerRadii();
  const { faucets: _faucets, faucetsToProductDetails } = useFaucetContext();
  const { customerSuppliedSinks, browsedSinks } = useSink();
  const { cutouts, tearOuts, chipMinimizer } = useOptionalItems();
  const {
    data: _cutoutLaborProducts,
    productsAsProductDetails: cutoutLaborProductsAsProductDetails,
  } = useCutoutLaborProducts();

  const { waterfallLaborFee } = useWaterfallLabor();

  const {
    optionalProducts: _optionalProducts,
    optionalProductsToProductDetails,
  } = useOptionalProductContext();
  const { browsedRefrigerators } = useRefrigeratorContext();
  const { dishwashersToProductDetails } = useDishwasherContext();
  const { cookingAppliancesToProductDetails } = useCookingAppliancesContext();

  const productCatalog = useMemo(() => {
    const tempProductCatalog: Record<number, ProductDetails> = {
      ...restorableCatalog,
    };

    [
      ...convertColorsToProductDetails(colors || []),
      ...convertProductsToProductDetailsList(ProductType.EDGE)(edges),
      ...convertProductsToProductDetailsList(ProductType.CORNER_RADIUS)(
        cornerRadii
      ),
      ...convertProductsToProductDetailsList(ProductType.DIAGONAL)(
        diagonalCorners
      ),
      ...convertProductsToProductDetailsList(ProductType.LABOR)(
        customerSuppliedSinks
      ),
      ...convertCutoutsToProductDetails(cutouts),
      ...cutoutLaborProductsAsProductDetails,
      ...dishwashersToProductDetails(),
      ...faucetsToProductDetails(),
      ...convertProductsToProductDetailsList(ProductType.LABOR)(
        waterfallLaborFee ? [waterfallLaborFee] : emptyArray
      ),
      ...optionalProductsToProductDetails(), //promos
      ...convertProductsToProductDetailsList(ProductType.REFRIGERATOR)(
        browsedRefrigerators
      ),
      ...convertProductsToProductDetailsList(ProductType.SINK)(browsedSinks),
      ...convertTearOutsToProductDetails(tearOuts),
      ...cookingAppliancesToProductDetails(),
      ...(chipMinimizer
        ? convertProductsToProductDetailsList(ProductType.CHIP_MINIMIZER)([
            chipMinimizer,
          ])
        : emptyArray),
      // TODO: add endCaps and endSplashes
    ].forEach((product) => {
      tempProductCatalog[product.productId] = product;
    });

    return tempProductCatalog;
  }, [
    colors,
    edges,
    cornerRadii,
    diagonalCorners,
    cutouts,
    tearOuts,
    browsedRefrigerators,
    chipMinimizer,
    cookingAppliancesToProductDetails,
    cutoutLaborProductsAsProductDetails,
    dishwashersToProductDetails,
    faucetsToProductDetails,
    optionalProductsToProductDetails,
    restorableCatalog,
    waterfallLaborFee,
    browsedSinks,
    customerSuppliedSinks,
  ]);

  return (
    <ProductDetailsContext.Provider
      value={{
        productCatalog,
        restorableCatalog,
        setRestorableCatalog,
      }}
    >
      {children}
    </ProductDetailsContext.Provider>
  );
};
